import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Upload, File, MessageCircle, Clock, User, Eye, RotateCcw, Send, X, ChevronDown, ChevronUp, FileText, Image, Archive, Search, Download } from 'lucide-react';
import { getRequest, postRequest } from '../../../services/apiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  searchQuery,
  onSearchChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    return options.filter(option =>
      option.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery]);

  const selectedOption = options.find(option => option.id === parseInt(value));

  const handleOptionSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    onSearchChange('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-300 text-left flex items-center justify-between bg-white hover:bg-gray-50"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-300"
                autoFocus
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleOptionSelect(option.id)}
                  className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0 ${
                    parseInt(value) === option.id
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                  title={option.name.length > 50 ? option.name : undefined}
                >
                  <div className="truncate">
                    {option.name}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-4 opacity-50" />
                <p>No options found</p>
                <p className="text-sm">Try adjusting your search</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RequisitionForm = ({ eventStatus, onClose, initialData }) => {
  const [activeTab, setActiveTab] = useState(
    eventStatus === 'not allocated' || eventStatus === 'new' ? 'upload' : 'history'
  );
  const [files, setFiles] = useState([]); // This will now hold files associated with the requisition
  const [history, setHistory] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null); // For new file upload
  const [remarks, setRemarks] = useState(initialData?.remarks || '');
  const [name, setName] = useState(initialData?.name || '');
  const [reviewComments, setReviewComments] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [expandedHistoryItem, setExpandedHistoryItem] = useState(null);
  const fileInputRef = useRef(null);

  const [selectedProject, setSelectedProject] = useState(initialData?.project || '');
  const [selectedShip, setSelectedShip] = useState(initialData?.ship || '');
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [taskSearchQueries, setTaskSearchQueries] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(initialData?.status || 'not allocated');
  // New state for selected date
  const [selectedDate, setSelectedDate] = useState(initialData?.date || new Date().toISOString().split('T')[0]);


  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [dynamicTasks, setDynamicTasks] = useState([[]]);

  const users = ['John Doe', 'Jane Smith', 'Alice Johnson', 'Bob Wilson', 'Sarah Chen'];
  const statusOptions = [
    { id: 1, name: 'Not Allocated' },
    { id: 2, name: 'Assigned' },
    { id: 3, name: 'Work in Progress' },
    { id: 4, name: 'Completed' },
    { id: 5, name: 'Re-offered' },
  ];

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getRequest('master/projects/');
        if (response.status === 'success' && response.data) {
          setProjects(response.data);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchShips = async () => {
      if (selectedProject) {
        try {
          const project = projects.find(p => p.name === selectedProject);
          if (project) {
            const response = await getRequest(`master/projects/?parent_id=${project.id}`);
            if (response.status === 'success' && response.data) {
              setShips(response.data);
            }
          }
        } catch (error) {
          console.error('Error fetching ships:', error);
          setShips([]);
        }
      } else {
        setShips([]);
      }
    };
    fetchShips();
  }, [selectedProject, projects]);

  useEffect(() => {
    if (eventStatus === 'not allocated' || eventStatus === 'new') {
      setActiveTab('upload');
    } else {
      setActiveTab('history');
    }
  }, [eventStatus]);


  const fetchTasksRecursively = useCallback(async (parentId, currentPath = []) => {
    const selectedShipObject = ships.find(ship => ship.name === selectedShip);
    if (!selectedShipObject) return [];

    const shipId = selectedShipObject.id;
    try {
      const response = await getRequest(`/pert/task-transaction-api/?${parentId ? `parent_task_id=${parentId}` : 'parent_task_id=null'}&project_id=${shipId}`);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return [];
    }
  }, [selectedShip, ships]);

  // Pre-populate form fields if initialData is provided (when editing an existing requisition)
  useEffect(() => {
    const prePopulateForm = async () => {
      if (initialData && initialData.id) {
        try {
          // Fetch the full requisition details including task hierarchy
          const response = await getRequest(`/pert/requisition-status-history/?requisition_id=${initialData.id}`);
          if (response && Array.isArray(response) && response.length > 0) {
            const latestRequisition = response[0].requisition; // Get the latest requisition details

            setName(latestRequisition.name || '');
            setRemarks(latestRequisition.remarks || '');
            setCurrentStatus(latestRequisition.status.toLowerCase() || 'not allocated');
            // Set selected date if available in initialData
            setSelectedDate(latestRequisition.date || new Date().toISOString().split('T')[0]);


            // Set Project
            if (latestRequisition.task && latestRequisition.task.project) {
              setSelectedProject(latestRequisition.task.project);
            }

            // Set Ship
            if (latestRequisition.task && latestRequisition.task.ship_name) {
              setSelectedShip(latestRequisition.task.ship_name);
            }

            // Set Tasks dynamically
            const taskPath = [];
            let currentTask = latestRequisition.task;

            // Build the task hierarchy from the deepest task up to the parent
            const buildTaskPath = (task) => {
              if (task && task.parent_task) {
                buildTaskPath(task.parent_task);
              }
              if (task) {
                taskPath.push(task);
              }
            };
            buildTaskPath(currentTask);

            const newDynamicTasks = [];
            const newSelectedTaskIds = [];
            const newSearchQueries = [];

            for (let i = 0; i < taskPath.length; i++) {
                const taskAtLevel = taskPath[i];
                const parentId = i > 0 ? taskPath[i-1].id : null;
                const tasksForDropdown = await fetchTasksRecursively(parentId);
                newDynamicTasks.push(tasksForDropdown);
                newSelectedTaskIds.push(taskAtLevel.id);
                newSearchQueries.push(''); // No search query needed for pre-population
            }

            setDynamicTasks(newDynamicTasks);
            setSelectedTaskIds(newSelectedTaskIds);
            setTaskSearchQueries(newSearchQueries);

            // Set existing files
            if (latestRequisition.files && latestRequisition.files.length > 0) {
              const existingFiles = latestRequisition.files.map(file => ({
                id: file.id,
                fileName: file.file.split('/').pop(),
                fileUrl: `https://pms-api.ilizien-projects-cdf.in${file.file}`
              }));
              setFiles(existingFiles);
            }
          }
        } catch (error) {
          console.error('Error pre-populating form:', error);
        }
      }
    };

    prePopulateForm();
  }, [initialData, fetchTasksRecursively, projects, ships]);


  // Fetch requisition history when component mounts or initialData changes
  useEffect(() => {
    const fetchRequisitionHistory = async () => {
      if (initialData && initialData.id) {
        try {
          const response = await getRequest(`/pert/requisition-status-history/?requisition_id=${initialData.id}`);
          if (response && Array.isArray(response)) {
            const formattedHistory = response.map(item => {
              const fileName = item.requisition.files.length > 0
                ? item.requisition.files[0].file.split('/').pop()
                : 'N/A';
              const fileUrl = item.requisition.files.length > 0
                ? `https://pms-api.ilizien-projects-cdf.in${item.requisition.files[0].file}`
                : null;

              return {
                id: item.id,
                fileName: fileName,
                fileUrl: fileUrl,
                uploadedBy: 'N/A', // Assuming uploadedBy is not directly in history API, adjust if needed
                uploadedAt: new Date(item.created_on).toLocaleString(),
                remarks: item.remarks || item.requisition.remarks || 'No remarks',
                status: item.status.toLowerCase(),
                reviewComments: null,
                reviewedBy: null,
                reviewedAt: null,
                project: item.requisition.task.project,
                ship: item.requisition.task.ship_name,
                task: item.requisition.task.name,
                name: item.requisition.name,
              };
            });
            setHistory(formattedHistory);
          }
        } catch (error) {
          console.error('Error fetching requisition history:', error);
          setHistory([]);
        }
      }
    };
    fetchRequisitionHistory();
  }, [initialData]);

  // Fetch initial tasks when project or ship changes for a new requisition
  useEffect(() => {
    const fetchInitialTasksForNewRequisition = async () => {
      // Only fetch initial tasks if it's a new requisition
      if (!initialData || !initialData.id) {
        if (selectedShip) {
          const tasks = await fetchTasksRecursively(null); // Fetch top-level tasks
          if (tasks.length > 0) {
            setDynamicTasks([tasks]);
            setSelectedTaskIds(['']); // Initialize with empty string for first level
            setTaskSearchQueries(['']); // Initialize search query for first level
          } else {
            setDynamicTasks([[]]); // No tasks found for this ship
            setSelectedTaskIds(['']);
            setTaskSearchQueries(['']);
          }
        } else {
          // If no ship is selected for a new requisition, clear tasks
          setDynamicTasks([[]]);
          setSelectedTaskIds([]);
          setTaskSearchQueries([]);
        }
      }
    };
    fetchInitialTasksForNewRequisition();
  }, [selectedShip, initialData, fetchTasksRecursively]);


  const handleTaskSelect = async (level, taskId) => {
    const newSelectedTaskIds = [...selectedTaskIds];
    newSelectedTaskIds[level] = taskId;

    for (let i = level + 1; i < newSelectedTaskIds.length; i++) {
      newSelectedTaskIds[i] = '';
    }
    const newDynamicTasks = dynamicTasks.slice(0, level + 1);

    const newSearchQueries = taskSearchQueries.slice(0, level + 1);
    if (newSearchQueries.length <= level + 1) {
      newSearchQueries.push('');
    }
    setTaskSearchQueries(newSearchQueries);
    setSelectedTaskIds(newSelectedTaskIds);

    const selectedShipObject = ships.find(ship => ship.name === selectedShip);
    if (!selectedShipObject || !taskId) {
      setDynamicTasks(newDynamicTasks);
      return;
    }

    const shipId = selectedShipObject.id;

    try {
      const response = await getRequest(`pert/task-transaction-api/?parent_task_id=${taskId}&project_id=${shipId}`);
      if (response.status === 'success' && response.data && response.data.length > 0) {
        newDynamicTasks.push(response.data);
      }
    } catch (error) {
      console.error('Error fetching sub-tasks:', error);
    }
    setDynamicTasks(newDynamicTasks);
  };

  const handleSearchChange = (level, query) => {
    const newSearchQueries = [...taskSearchQueries];
    newSearchQueries[level] = query;
    setTaskSearchQueries(newSearchQueries);
  };

  const filteredDynamicTasks = useMemo(() => {
    return dynamicTasks.map((tasksAtLevel, index) => {
      const query = taskSearchQueries[index]?.toLowerCase() || '';
      if (!query) {
        return tasksAtLevel;
      }
      return tasksAtLevel.filter(task =>
        task.name.toLowerCase().includes(query)
      );
    });
  }, [dynamicTasks, taskSearchQueries]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    const finalSelectedTaskId = selectedTaskIds[selectedTaskIds.length - 1];
    const lastTaskLevel = dynamicTasks[dynamicTasks.length - 1];
    const finalSelectedTaskName = lastTaskLevel?.find(task => task.id === parseInt(finalSelectedTaskId))?.name || '';

    const selectedStatusObject = statusOptions.find(option => option.name.toLowerCase() === currentStatus.toLowerCase());
    const statusId = selectedStatusObject ? selectedStatusObject.id : null;

    if (!remarks.trim() || !name.trim() || !selectedProject || !selectedShip || selectedTaskIds.some(id => !id) || selectedTaskIds.length === 0 || !statusId || !selectedDate) {
      toast.warn('Please fill all required fields, and choose a task, status, and date.');
      return;
    }

    if (!selectedFile && initialData && files.length === 0) {
      toast.warn('Please select a file to upload or update.');
      return;
    }

    // Date validation: Check if selectedDate is a Saturday or Sunday
    const dateObj = new Date(selectedDate);
    const dayOfWeek = dateObj.getDay(); // 0 for Sunday, 6 for Saturday
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      toast.error('Saturdays and Sundays are not allowed. Please select a weekday.');
      return;
    }


    const formData = new FormData();
    if (selectedFile) {
        formData.append('files', selectedFile);
    }
    formData.append('task', finalSelectedTaskId);
    formData.append('date', selectedDate); // Use the selectedDate here
    formData.append('remarks', remarks);
    formData.append('name', name);
    formData.append('status', statusId); // Use the numeric ID for status

    if (initialData && initialData.id) {
        formData.append('id', initialData.id); // Add requisition ID for update
    }


    try {
        const response = await postRequest('pert/requisition-files/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        if (response && (response.status === 201 || response.status === 200) && response.data) {
            toast.success(initialData ? 'Requisition updated successfully!' : 'File uploaded successfully!');

            const newFileOrUpdatedRequisition = {
                id: response.data.id || Date.now(),
                fileName: selectedFile ? selectedFile.name : (files[0]?.fileName || 'N/A'),
                uploadedBy: 'Current User', // This might need to be adjusted based on actual API response for uploadedBy
                uploadedAt: new Date().toLocaleString(),
                remarks: remarks,
                name: name,
                status: currentStatus,
                reviewComments: null,
                reviewedBy: null,
                reviewedAt: null,
                project: selectedProject,
                ship: selectedShip,
                task: finalSelectedTaskName,
                fileUrl: selectedFile ? URL.createObjectURL(selectedFile) : (files[0]?.fileUrl || null)
            };

            if (initialData) {
              // If updating, update the history to reflect the latest status/data
              setHistory(prevHistory => {
                const existingIndex = prevHistory.findIndex(item => item.id === newFileOrUpdatedRequisition.id);
                if (existingIndex > -1) {
                  const updatedHistory = [...prevHistory];
                  updatedHistory[existingIndex] = { ...updatedHistory[existingIndex], ...newFileOrUpdatedRequisition };
                  return updatedHistory;
                }
                return [newFileOrUpdatedRequisition, ...prevHistory]; // Add as new if not found
              });
            } else {
              setHistory([...history, newFileOrUpdatedRequisition]);
            }

            setSelectedFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }

            onClose(); // Close the form after successful operation

            if (eventStatus && initialData) { // If it's an existing event, switch to history tab
                setActiveTab('history');
            }
        } else {
            console.error('File operation failed: Unexpected success response structure or status.', response);
            toast.error('File operation failed: Unexpected response from server. Please try again.');
        }
    } catch (error) {
        console.error('Error uploading/updating file:', error);
        toast.error(`Error uploading/updating file: ${error.message || 'Please check your connection.'}`);
    }
  };


  const handleReview = (fileId, action) => {
    setHistory(history.map(item => {
      if (item.id === fileId) {
        return {
          ...item,
          status: action === 'approve' ? 'approved' : 'rejected',
          reviewComments: reviewComments,
          reviewedBy: 'Current Reviewer',
          reviewedAt: new Date().toLocaleString()
        };
      }
      return item;
    }));
    setReviewComments('');
  };

  const handleReopen = (fileId) => {
    if (assignTo) {
      setHistory(history.map(item => {
        if (item.id === fileId) {
          return {
            ...item,
            status: 'reopened',
            assignedTo: assignTo,
            reopenedAt: new Date().toLocaleString()
          };
        }
        return item;
      }));
      setAssignTo('');
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) {
      return <Image className="w-5 h-5 text-blue-500" />;
    } else if (['zip', 'rar', '7z'].includes(extension)) {
      return <Archive className="w-5 h-5 text-purple-500" />;
    } else {
      return <FileText className="w-5 h-5 text-green-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'reviewed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'reopened': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'not allocated': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'assigned': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'work in progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 're-offered': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const shouldShowAllTabs = !(eventStatus === 'not allocated' || eventStatus === 'new');

  // Function to check if a date is a weekend
  const isWeekend = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDay(); // Sunday is 0, Saturday is 6
    return day === 0 || day === 6;
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate && isWeekend(newDate)) {
      toast.error('Saturdays and Sundays are not allowed. Please select a weekday.');
      setSelectedDate(''); // Clear the selected date if it's a weekend
    } else {
      setSelectedDate(newDate);
    }
  };

  return (
    <div className="max-w-6xl mx-auto bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden backdrop-blur-sm bg-opacity-95">
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-8 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>

          <div className="absolute -right-20 -top-20 w-40 h-40 bg-white opacity-10 rounded-full animate-pulse"></div>
          <div className="absolute -left-16 -bottom-16 w-32 h-32 bg-white opacity-5 rounded-full animate-bounce"></div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex border-b border-gray-200 bg-gray-50">
          {(() => {
            const tabsToRender = [];

            tabsToRender.push({ id: 'upload', label: 'Upload Files', icon: Upload });

            if (shouldShowAllTabs) {
              tabsToRender.push({ id: 'review', label: 'Review Files', icon: Eye });
              tabsToRender.push({ id: 'history', label: 'File History', icon: Clock });
            }

            return tabsToRender.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-3 py-4 px-6 font-semibold transition-all duration-300 transform hover:scale-105 ${
                    activeTab === tab.id
                      ? 'bg-white text-blue-600 border-b-4 border-blue-600 shadow-lg'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            });
          })()}
        </div>
        <div className="p-8">
          {activeTab === 'upload' && (
            <div className="space-y-8 animate-fade-in">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{initialData ? 'Update Task Files' : 'Upload Task Files'}</h2>
                <p className="text-gray-600">Select files and add remarks for your assigned tasks</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="project-select" className="block text-lg font-semibold text-gray-700 mb-2">Project</label>
                  <select
                    id="project-select"
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-300"
                  >
                    <option value="">Select a Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.name}>{project.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="ship-select" className="block text-lg font-semibold text-gray-700 mb-2">Ship</label>
                  <select
                    id="ship-select"
                    value={selectedShip}
                    onChange={(e) => setSelectedShip(e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-300"
                  >
                    <option value="">Select a Ship</option>
                    {ships.map(ship => (
                      <option key={ship.id} value={ship.name}>{ship.name}</option>
                    ))}
                  </select>
                </div>

               {filteredDynamicTasks.map((tasksAtLevel, index) => (
                  tasksAtLevel.length > 0 && (
                    <div key={`task-level-${index}`}>
                      <label htmlFor={`task-select-${index}`} className="block text-lg font-semibold text-gray-700 mb-2">
                        {index === 0 ? 'Task Name' : `Sub-Task ${index}`}
                      </label>
                      <CustomDropdown
                        options={tasksAtLevel}
                        value={selectedTaskIds[index] || ''}
                        onChange={(value) => handleTaskSelect(index, value)}
                        placeholder={`Select a ${index === 0 ? 'Task' : `Sub-Task`}`}
                        searchPlaceholder={`Search ${index === 0 ? 'Task' : `Sub-Task ${index}`}...`}
                        searchQuery={taskSearchQueries[index] || ''}
                        onSearchChange={(query) => handleSearchChange(index, query)}
                      />
                    </div>
                  )
                ))}
              </div>

              <div className="space-y-4">
                <label htmlFor="name-input" className="block text-lg font-semibold text-gray-700">Name</label>
                <input
                  id="name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter a name for the requisition..."
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-300"
                />
              </div>

              <div className="space-y-4">
                <label htmlFor="status-select" className="block text-lg font-semibold text-gray-700">Status</label>
                <select
                  id="status-select"
                  value={currentStatus}
                  onChange={(e) => setCurrentStatus(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-300"
                >
                  {statusOptions.map(option => (
                    <option key={option.id} value={option.name.toLowerCase()}>{option.name}</option>
                  ))}
                </select>
              </div>

              {/* Date Picker Input */}
              <div className="space-y-4">
                <label htmlFor="date-picker" className="block text-lg font-semibold text-gray-700">Requisition Date</label>
                <input
                  id="date-picker"
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange} // Use the new handler here
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-300"
                />
              </div>

              {/* Display already added files */}
              {files.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-700">Existing Files:</h3>
                  {files.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-3">
                        {getFileIcon(file.fileName)}
                        <p className="text-gray-700">{file.fileName}</p>
                      </div>
                      {file.fileUrl && (
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 flex items-center gap-1"
                          title="Download existing file"
                        >
                          <Download className="w-4 h-4" /> View
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <div className={`border-3 border-dashed rounded-2xl p-12 text-center transition-all duration-300 hover:scale-105 ${
                  selectedFile
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
                }`}>
                  <div className="flex flex-col items-center space-y-4">
                    <div className={`p-4 rounded-full ${selectedFile ? 'bg-green-200' : 'bg-blue-200'} transition-colors duration-300`}>
                      {selectedFile ? (
                        <File className="w-12 h-12 text-green-600" />
                      ) : (
                        <Upload className="w-12 h-12 text-blue-600" />
                      )}
                    </div>
                    {selectedFile ? (
                      <div className="space-y-2">
                        <p className="text-lg font-semibold text-green-700">File Selected!</p>
                        <p className="text-gray-600">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-lg font-semibold text-gray-700">{initialData ? 'Drop new file here or click to update existing' : 'Drop files here or click to upload'}</p>
                        <p className="text-gray-500">Support for PDF, DOC, ZIP, and image files</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <label className="block text-lg font-semibold text-gray-700">Remarks</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Add your remarks or description for this file..."
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-300 resize-none h-32"
                />
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleUpload}
                  disabled={!remarks.trim() || !name.trim() || !selectedProject || !selectedShip || selectedTaskIds.some(id => !id) || selectedTaskIds.length === 0 || !currentStatus || !selectedDate || (!selectedFile && files.length === 0)}
                  className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-white transition-all duration-300 transform hover:scale-105 ${
                    (remarks.trim() && name.trim() && selectedProject && selectedShip && selectedTaskIds.every(id => id) && selectedTaskIds.length > 0 && currentStatus && selectedDate && (selectedFile || files.length > 0))
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Send className="w-5 h-5" />
                  {initialData ? 'Update File' : 'Upload File'}
                </button>
              </div>
            </div>
          )}

          {shouldShowAllTabs && activeTab === 'review' && (
            <div className="space-y-8 animate-fade-in">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Review Files</h2>
                <p className="text-gray-600">Review submitted files and provide feedback</p>
              </div>

              <div className="grid gap-6">
                {history.filter(item => item.status === 'pending').map(item => (
                  <div key={item.id} className="bg-gradient-to-r from-white to-blue-50 rounded-2xl p-6 border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-102">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        {getFileIcon(item.fileName)}
                        <div>
                          <h3 className="font-bold text-lg text-gray-800">{item.fileName}</h3>
                          <p className="text-gray-600">Uploaded by {item.uploadedBy}</p>
                          <p className="text-sm text-gray-500">{item.uploadedAt}</p>
                          {item.project && <p className="text-sm text-gray-500">Project: {item.project}</p>}
                          {item.ship && <p className="text-sm text-gray-500">Ship: {item.ship}</p>}
                          {item.task && <p className="text-sm text-gray-500">Task: {item.task}</p>}
                          {item.name && <p className="text-sm text-gray-500">Requisition Name: {item.name}</p>}
                        </div>
                      </div>
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(item.status)}`}>
                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </span>
                    </div>

                    <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <h4 className="font-semibold text-gray-700 mb-2">Remarks:</h4>
                      <p className="text-gray-600">{item.remarks}</p>
                    </div>

                    <div className="space-y-4">
                      <textarea
                        value={reviewComments}
                        onChange={(e) => setReviewComments(e.target.value)}
                        placeholder="Add your review comments..."
                        className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-300 resize-none h-24"
                      />

                      <div className="flex gap-4 justify-end">
                        <button
                          onClick={() => handleReview(item.id, 'reject')}
                          className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-300 transform hover:scale-105"
                        >
                          <X className="w-4 h-4" />
                          Reject
                        </button>
                        <button
                          onClick={() => handleReview(item.id, 'approve')}
                          className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all duration-300 transform hover:scale-105"
                        >
                          <Send className="w-4 h-4" />
                          Approve
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {history.filter(item => item.status === 'pending').length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Eye className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-xl">No files pending review</p>
                    <p>All submitted files have been reviewed</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {shouldShowAllTabs && activeTab === 'history' && (
            <div className="space-y-8 animate-fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">File History</h2>
                  <p className="text-gray-600">View complete history of all uploaded files</p>
                </div>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-all duration-300"
                >
                  {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {showHistory ? 'Collapse' : 'Expand'} All
                </button>
              </div>

              <div className="grid gap-4">
                {history.map(item => (
                  <div key={item.id} className="bg-white rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden">
                    <div
                      className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                      onClick={() => setExpandedHistoryItem(expandedHistoryItem === item.id ? null : item.id)}
                    >
                      <div className="flex items-center gap-4">
                        {getFileIcon(item.fileName)}
                        <div>
                          <h3 className="font-bold text-lg text-gray-800">{item.fileName}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {item.uploadedBy}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {item.uploadedAt}
                            </span>
                          </div>
                          {item.project && <p className="text-sm text-gray-500">Project: {item.project}</p>}
                          {item.ship && <p className="text-sm text-sm text-gray-500">Ship: {item.ship}</p>}
                          {item.task && <p className="text-sm text-gray-500">Task: {item.task}</p>}
                          {item.name && <p className="text-sm text-gray-500">Requisition Name: {item.name}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(item.status)}`}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                        {expandedHistoryItem === item.id ?
                          <ChevronUp className="w-5 h-5 text-gray-400" /> :
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        }
                      </div>
                    </div>

                    {expandedHistoryItem === item.id && (
                      <div className="px-6 pb-6 space-y-4 border-t border-gray-100 bg-gray-50 animate-fade-in">
                        <div className="pt-4">
                          <h4 className="font-semibold text-gray-700 mb-2">Original Remarks:</h4>
                          <p className="text-gray-600 bg-white p-3 rounded-lg border">{item.remarks}</p>
                        </div>

                        {item.reviewComments && (
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2">Review Comments:</h4>
                            <p className="text-gray-600 bg-white p-3 rounded-lg border">{item.reviewComments}</p>
                            <p className="text-sm text-gray-500 mt-2">
                              Reviewed by {item.reviewedBy} on {item.reviewedAt}
                            </p>
                          </div>
                        )}

                        {item.fileUrl && (
                          <div className="pt-4">
                            <a
                              href={item.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
                            >
                              <Download className="w-4 h-4" />
                              Download File
                            </a>
                          </div>
                        )}

                        {(item.status === 'rejected' || item.status === 'approved') && (
                          <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                            <select
                              value={assignTo}
                              onChange={(e) => setAssignTo(e.target.value)}
                              className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            >
                              <option value="">Select user to reassign...</option>
                              {users.map(user => (
                                <option key={user} value={user}>{user}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleReopen(item.id)}
                              disabled={!assignTo}
                              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 ${
                                assignTo
                                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600'
                                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              <RotateCcw className="w-4 h-4" />
                              Reopen Task
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequisitionForm;